<template>
<div class="c-process-item">
	<can i="customize" :a="processType">
		<div v-if="processType === 'ProcessTemplate'" class="c-process-item__process-template-info s-text t-margin-bottom-m">
			<h2>Processmall från Installatörsföretagen</h2>
			<p>Nedan är ett förslag på process som du kan utgå från för att skapa en process för ditt företag. När du anpassat en process och publicerat den så blir den synlig för användarna på ditt företag.</p>
			<p>Du som administratör kommer alltid åt processmallen från din anpassade process. Där får du även meddelande om uppdateringar av innehållet så du kan se om du behöver anpassa er egen process. Klicka på knappen nedan för att börja skapa er egen process.</p>
			<button type="submit" v-if="status === 'Published' && visibility === 'Public'" class="c-button c-button--primary" @click="toggleCustomizeModal">Använd mall för att skapa process</button>
			<modal :show="showCustomizeModal" @closeModal="toggleCustomizeModal">
				<div class="c-modal__content t-background-white t-text-align-center">
					<h1 class="t-text-style-alpha t-margin-bottom-s">Skapa anpassad process?</h1>
					<p>Du kommer nu att skapa en kopia av processmallen som du kan anpassa till ditt företag. Processmallen från Installatörsföretagen finns kvar och påverkas inte av dina ändringar.</p>
					<form @submit.prevent="handleSubmit" class="c-form">
						<button class="c-button c-button--linkstyle c-button--reset t-margin-top-m" @click="toggleCustomizeModal">Avbryt</button>
						<button type="submit" class="c-button c-button--primary t-margin-top-m" @click="setSubmitType('customize')">Anpassa process</button>			
					</form>
				</div>
			</modal>	
		</div>
	</can>
    <template v-if="dataMode === 'view' && id !== '0'">
		<div class="c-process-item__process-info o-grid">
			<div data-size="sm:12 md:8 lg:8" class="o-grid__column">
				<div class="t-text-align-left">
					<h1 class="t-text-style-alpha">{{name}}</h1>
					<div class="c-process-item__actions">
						<can i="manage" :a="processType">
							<button class="c-button c-button--linkstyle c-button--reset" @click="goToEditMode">
								<vue-icon name="edit" size="c-icon--17px"></vue-icon>
								<span class="c-process-item__button">Ändra process</span>
							</button>
						</can>	
						<button class="c-button c-button--linkstyle c-button--reset" type="button" @click="toggleChangeLog">
							<vue-icon name="time"></vue-icon>
							<span class="c-process-item__button">{{buttonText}}</span>
						</button>
					</div>
				</div>
			</div>
			<div data-size="sm:12 md:4 lg:4" class="o-grid__column">
				<div class="t-text-align-right">
					<can i="manage" :a="processType">
						<toggle-status 
							type="Process" 
							:status="status" 
							:process-id="processId"
							@publish="publishProcess"
							@unpublish="unpublishProcess"
							v-if="status === 'Unpublished'">
						</toggle-status>
					</can>
				</div>
			</div>
			<div data-size="sm:12 md:12 lg:12" class="o-grid__column">
				<collapse-transition>
					<change-log-data-provider 
						:process-id="id" 
						v-if="showChangeLog">
						<change-log
							slot-scope="dataProvider"
							:process-id="id"
							:process-type="processType"
							:change-log-entries="dataProvider.changeLogEntries"
							:unread-notifications = "showConsentButton"
							@toggleChangeLog="toggleChangeLog"
							@create="dataProvider.create"
							@logReadConsent="logReadConsent"
						></change-log>
					</change-log-data-provider>
				</collapse-transition>
			</div>
			<div class="o-grid__column">
				<div v-html="description" class="s-text s-text--vue t-margin-top-s"></div>
			</div>
		</div>
    </template>
	<template v-if="$can('create', 'NewProcess') || $can('manage', processType)">
		<template v-if="dataMode === 'create' || dataMode === 'edit'">
			<h1 v-if="dataMode == 'create'" class="t-text-style-alpha t-margin-bottom-s">Skapa ny process</h1>
			<div class="c-process-item__header">
				<h1 class="t-text-style-alpha" v-if="dataMode == 'edit'">Ändra process</h1>
				<can i="manage" :a="processType">
					<toggle-status 
						type="Process" 
						:status="status" 
						:process-id="processId"
						@publish="publishProcess"
						@unpublish="unpublishProcess"
						v-if="dataMode === 'edit'">
					</toggle-status>
				</can>
			</div>
			<form @submit.prevent="handleSubmit" class="c-form c-process-item__form t-margin-top-s">
				<fieldset>
					<div class="c-form__item">
						<vue-input label="Namn på process" input-id="name" :value="name" ref="name"></vue-input>
					</div>
					<div class="c-form__item">
						<vue-input label="Beskrivning" input-id="description" :value="description" ref="description" type="textarea"></vue-input>
					</div>
					<div class="t-space-between">
						<div class="t-margin-bottom-xs">
							<button type="submit" v-if="dataMode === 'create'" class="c-button c-button--primary" @click="setSubmitType('create')">Skapa process</button>
							<router-link v-if="dataMode === 'create'" class="c-button c-button--linkstyle c-button--reset" to="/">Avbryt</router-link>
							<button type="submit" v-if="dataMode === 'edit'" class="c-button c-button--primary" @click="setSubmitType('edit')">Spara ändringar</button>
							<button type="button" v-if="dataMode === 'edit'" class="c-button c-button--linkstyle c-button--reset" @click.prevent="cancelEditProcess">Avbryt</button>
						</div>
						<button type="submit" v-if="dataMode === 'edit'" class="c-button c-button--linkstyle c-button--linkstyle__secondary c-button--reset t-margin-bottom-xs" @click="toggleDeleteModal">
							<vue-icon name="delete" size="c-icon--17px"></vue-icon>
							<span class="c-button__title">Ta bort process</span>
						</button>
						<modal :show="showDeleteModal" @closeModal="toggleDeleteModal">
							<div class="c-modal__content t-background-white t-text-align-center">
								<h1 class="t-text-style-alpha">Bekräfta borttagning av process</h1>
								<form @submit.prevent="handleSubmit" class="c-form">
									<button class="c-button c-button--linkstyle c-button--reset t-margin-top-m" @click="toggleDeleteModal">
										<span>Avbryt</span>
									</button>
									<button type="submit" class="c-button c-button--primary t-margin-top-m" @click="setSubmitType('delete')">
										<span>Ta bort</span>
									</button>
								</form>
							</div>
						</modal>
					</div>
				</fieldset>
			</form>
		</template>
	</template>
	<process-steps-data-provider v-if="id !== '0'" :process-id="id" class="t-margin-top-m">
		<process-steps 
			slot-scope="dataProvider" 
			:process-id="id" 
			:process-steps="dataProvider.processSteps" 
			:process-type="processType"
			:show-create="dataProvider.showCreate"
			@createProcessStep="dataProvider.createProcessStep" 
			@deleteProcessStep="dataProvider.deleteProcessStep" 
			@editProcessStep="dataProvider.editProcessStep"
			@setShowCreate="dataProvider.setShowCreate"
			@sortProcessStep="dataProvider.sortProcessStep"
			@publish="dataProvider.publish"
			@unpublish="dataProvider.unpublish">
		</process-steps>
	</process-steps-data-provider>
</div>
</template>

<script>
import VueInput from '@/components/vue-components/vue-input/VueInput.vue';
import ProcessStepsDataProvider from '@/components/INQ/process-steps-data-provider/ProcessStepsDataProvider.vue';
import ProcessSteps from '@/components/INQ/process-steps/ProcessSteps.vue';
import ToggleStatus from '@/components/INQ/toggle-status/ToggleStatus.vue';
import ChangeLog from '@/components/INQ/change-log/ChangeLog.vue';
import ChangeLogDataProvider from '@/components/INQ/change-log-data-provider/ChangeLogDataProvider.vue';
import VueIcon from '@/components/vue-components/vue-icon/VueIcon.vue';
import Modal from '@/components/vue-components/modal/Modal.vue';
import { CollapseTransition } from 'vue2-transitions';
export default {
    name: 'ProcessItem',
    props: {
        id: {
            type: String,
            default: '0',
        },
        mode: {
            type: String,
            default: 'view',
        },
        name: {
            type: String,
            default: '',
        },
        description: {
            type: String,
            default: '',
		},
		status: {
			type: String,
			default: 'Unpublished'
		},
		processId: {
			type: String,
			default: ''
		},
		category: {
			type: String,
			default: ''
		},
		originalProcess: {
			type: String,
		},
		visibility: {
			type: String, 
			default: ''
		},
		processType: {
			type: String,
			default: 'NewProcess',
		},
		showConsentButton: {
			type: Boolean,
			default: false
		},
		showChangeLog: {
			type: Boolean,
			default: false
		},
		expandChangeLogFromStart: {
			type: Boolean,
			default: false
		}
    },
    watch: {
        mode: function (val) {
            this.dataMode = val;
		},
		description: function () {
            this.dataDescription = this.description;
        },
    },
    components: {
        VueInput,
        ProcessStepsDataProvider,
		ProcessSteps,
		ToggleStatus,
		ChangeLog,
		CollapseTransition,
		VueIcon,
		ChangeLogDataProvider,
		Modal
    },
    data() {
        return {
            dataMode: '',
			submitType: '',
			buttonText: 'Ändringshistorik',
			showChangeLog: false,
			showDeleteModal: false,
			showCustomizeModal: false,
			descriptionData: this.description
        }
    },
    methods: {
		setSubmitType(type) {
            this.submitType = type;
        },
        create() {
            let name = this.$refs.name.getValue();
            let description = this.$refs.description.getValue();

            this.$emit('create', {
                name: name,
                description: description,
				category: this.category
            });
		},
		customize() {
			this.$emit('customize', {
				id: this.id
			});	
        },
		delete() {
			this.$emit('delete', {
				id: this.id
			});	
        },
        edit() {
            let name = this.$refs.name.getValue();
            let description = this.$refs.description.getValue();

            this.$emit('edit', {
                id: this.id,
                name: name,
                description: description
            });
            this.goToViewMode();
		},
		publishProcess() {
			this.$emit('publish', {
				id: this.id
			});
		},
		unpublishProcess() {
			this.$emit('unpublish', {
				id: this.id
			});	
		},
		goToCreateMode() {
			this.dataMode = 'create';
		},
        goToEditMode() {
            this.dataMode = 'edit';
        },
        goToViewMode() {
            this.dataMode = 'view';
        },
        cancelEditProcess() {
            this.goToViewMode();
        },
		handleSubmit() {
            switch (this.submitType) {
                case 'create':
                    this.create();
                    break;
                case 'edit':
                    this.edit();
                    break;
                case 'delete':
                    this.delete();
					break;
				case 'customize':
					this.customize();
					break;
            }
		},
		toggleChangeLog() {
			this.showChangeLog = !this.showChangeLog;
			if (this.showChangeLog) {
				this.buttonText = 'Stäng ändringshistorik';
			} else {
				this.buttonText = 'Ändringshistorik';
			}
		},
			logReadConsent() {
			  this.$emit("logReadConsent");
		},
		toggleDeleteModal() {
			this.showDeleteModal = !this.showDeleteModal;
		},
		toggleCustomizeModal() {
			this.showCustomizeModal = !this.showCustomizeModal;
		}
    },
    created() {
		this.dataMode = this.$props.mode;
		if (this.expandChangeLogFromStart) {
			this.toggleChangeLog();
		}
    }
}
</script>
